import { useCallback } from 'react'
import { CheckboxLabelsOptions, TableFilterItem } from '../types'

/**
 * Creates a resolver for the `true` and `false` options used by boolean
 * filters. The resolver applies custom labels, omits choices configured as
 * `false`, and can transform each boolean into a caller-defined value.
 */
export function useBooleanFilterItems() {
  return useCallback(
    (
      labels: CheckboxLabelsOptions | undefined,
      value?: (labelIsTrue: boolean) => boolean | React.Key
    ) => {
      const resolved = labels ?? { false: 'False', true: 'True' }

      const child = (which: 'True' | 'False'): TableFilterItem | undefined => {
        const whichIsTrue = which === 'True'
        const key = whichIsTrue ? 'true' : 'false'
        const label = resolved[key]
        if (label === false) return undefined
        return {
          text: typeof label === 'string' ? label : label?.text ?? which,
          tooltip: typeof label === 'string' ? undefined : label?.tooltip,
          value: value ? value(whichIsTrue) : whichIsTrue,
        }
      }

      return (['True', 'False'] as const).reduce((filters, label) => {
        const option = child(label)
        if (option) filters.push(option)
        return filters
      }, Array<TableFilterItem>())
    },
    []
  )
}
